import type {
  FloatingOptions,
  Locale,
  Options,
  PartialDeep,
  Styles,
} from "react-joyride";
import tourTokens from "./tour.tokens";
import {
  DEFAULT_TARGET_WAIT_MS,
  TOUR_Z_INDEX,
  scrollOffsetFor,
} from "../runtime/constants";

const t = tourTokens;

const SCROLL_DURATION_MS = 300;

export const TOUR_LOCALE: Locale = {
  back: "Back",
  close: "End tour",
  last: "Done",
  next: "Next",
  skip: "Skip",
};

interface JoyrideOptionsInput {
  hasOfferBanner: boolean;
  prefersReducedMotion: boolean;
}

export const buildJoyrideOptions = ({
  hasOfferBanner,
  prefersReducedMotion,
}: JoyrideOptionsInput): Partial<Options> => ({
  zIndex: TOUR_Z_INDEX,
  primaryColor: t.actions.primaryBg,
  overlayColor: t.overlay.color,
  arrowColor: t.arrow.color,
  arrowSize: t.arrow.size,
  arrowBase: t.arrow.base,
  spotlightRadius: t.overlay.spotlightRadius,
  spotlightPadding: t.overlay.spotlightPadding,
  targetWaitTimeout: DEFAULT_TARGET_WAIT_MS,
  scrollOffset: scrollOffsetFor(hasOfferBanner),
  scrollDuration: prefersReducedMotion ? 0 : SCROLL_DURATION_MS,
  overlayClickAction: false,
  blockTargetInteraction: true,
  dismissKeyAction: "close",
  closeButtonAction: "skip",
  disableFocusTrap: false,
  skipBeacon: true,
});

export const buildFloatingOptions = (): Partial<FloatingOptions> => ({
  shiftOptions: { crossAxis: true, padding: t.overlay.viewportPadding },
  flipOptions: { padding: t.overlay.viewportPadding },
});

export const buildJoyrideStyles = (): PartialDeep<Styles> => ({
  floater: {
    filter: "none",
  },
  tooltip: {
    padding: 0,
    borderRadius: t.tooltip.radius,
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  tooltipContainer: {
    textAlign: "left",
  },
  spotlight: {
    stroke: t.overlay.cutoutBorderColor,
    strokeWidth: t.overlay.cutoutBorderWidth,
  },
});

type Widen<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T;

type Viewport<G> = { [K in Exclude<keyof G, "mobile">]: Widen<G[K]> };

/** Base token values, with the group's `mobile` overrides folded in. */
const forViewport = <G extends { readonly mobile?: Record<string, unknown> }>(
  group: G,
  isMobile: boolean,
): Viewport<G> => {
  const { mobile, ...base } = group;
  return {
    ...base,
    ...(isMobile && mobile ? mobile : {}),
  } as unknown as Viewport<G>;
};

const focusVisible = {
  "&:focus-visible": {
    outline: `${t.focus.width} solid ${t.focus.color}`,
    outlineOffset: t.focus.offset,
  },
};

const buildStyles = (isMobile: boolean) => {
  const glass = forViewport(t.glass, isMobile);
  const tooltip = forViewport(t.tooltip, isMobile);
  const close = forViewport(t.close, isMobile);
  const title = forViewport(t.title, isMobile);
  const body = forViewport(t.body, isMobile);
  const footer = forViewport(t.footer, isMobile);
  const progress = forViewport(t.progress, isMobile);
  const actions = forViewport(t.actions, isMobile);

  const buttonBase = {
    minWidth: "auto",
    height: actions.height,
    borderRadius: actions.radius,
    textTransform: "none" as const,
    fontFamily: "inherit",
    fontSize: actions.fontSize,
    fontWeight: actions.fontWeight,
    letterSpacing: "normal",
    lineHeight: "normal",
    boxShadow: "none",
    ...(isMobile && {
      position: "relative" as const,
      "&::after": {
        content: '""',
        position: "absolute" as const,
        top: "50%",
        left: 0,
        right: 0,
        transform: "translateY(-50%)",
        height: actions.tapArea,
      },
    }),
    ...focusVisible,
  };

  return {
    /** Values components need directly, resolved for this viewport. */
    metrics: {
      closeIconSize: close.iconSize,
      chevronSize: actions.chevronSize,
      maxDots: progress.maxDots,
    },

    card: {
      boxSizing: "border-box",
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: tooltip.width,
      maxWidth: tooltip.maxWidth,
      p: tooltip.padding,
      gap: tooltip.gap,
      borderRadius: tooltip.radius,
      boxShadow: tooltip.shadow,
      background: glass.background,
      backdropFilter: glass.blur,
      WebkitBackdropFilter: glass.blur,
      border: glass.border,
    },

    closeButton: {
      position: "absolute",
      top: close.offset,
      right: close.offset,
      width: close.size,
      height: close.size,
      p: 0,
      borderRadius: close.radius,
      color: close.color,
      "&:hover": {
        backgroundColor: close.hoverBg,
        color: close.hoverColor,
      },
      ...focusVisible,
    },

    title: {
      m: 0,
      fontSize: title.fontSize,
      fontWeight: title.fontWeight,
      color: title.color,
      pr: title.paddingRight,
    },

    content: {
      m: 0,
      fontSize: body.fontSize,
      fontWeight: body.fontWeight,
      lineHeight: body.lineHeight,
      color: body.color,
    },

    footer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: footer.gap,
      pt: footer.paddingTop,
    },

    progressGroup: {
      display: "flex",
      alignItems: "center",
      gap: progress.gap,
      minWidth: 0,
    },

    dotRow: {
      display: "flex",
      gap: progress.dotGap,
    },

    dot: (isActive: boolean) => ({
      width: progress.dotSize,
      height: progress.dotSize,
      borderRadius: "50%",
      flexShrink: 0,
      backgroundColor: isActive ? progress.dotActive : progress.dotInactive,
    }),

    counter: {
      fontSize: progress.counterFontSize,
      fontWeight: progress.counterFontWeight,
      color: progress.counterColor,
      lineHeight: "normal",
      whiteSpace: "nowrap",
    },

    actions: {
      display: "flex",
      alignItems: "center",
      gap: actions.gap,
      flexShrink: 0,
    },

    ghostButton: {
      "&.MuiButton-root": {
        ...buttonBase,
        px: actions.ghostPaddingX,
        border: actions.ghostBorder,
        background: actions.ghostBg,
        color: actions.ghostColor,
        "&:hover": {
          border: actions.ghostBorder,
          borderColor: actions.ghostHoverBorderColor,
          color: actions.ghostHoverColor,
          background: actions.ghostBg,
        },
      },
    },

    primaryButton: {
      "&.MuiButton-root": {
        ...buttonBase,
        px: actions.primaryPaddingX,
        gap: actions.primaryGap,
        background: actions.primaryBg,
        color: actions.primaryColor,
        "&:hover": {
          background: actions.primaryHoverBg,
          boxShadow: "none",
        },
      },
    },

  } as const;
};

export type TourStyles = ReturnType<typeof buildStyles>;

const byViewport = new Map<boolean, TourStyles>();

/** The tour only has two viewports, so both are built at most once. */
export const tourStylesFor = (isMobile: boolean): TourStyles => {
  const cached = byViewport.get(isMobile);
  if (cached) return cached;
  const built = buildStyles(isMobile);
  byViewport.set(isMobile, built);
  return built;
};

/** The modal has no mobile variant, so its styles are viewport-independent. */
export const modalStyles = {
  /** Layout for the card's contents — see `paper` on why it is not on the card. */
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: t.modal.gap,
  },

  paper: {
    boxSizing: "border-box",
    /*
      No width / maxWidth / margin here. `AppModal` sizes the paper from its
      `size` step, and it does so responsively — a flat `width: 420` loses to
      that media query above 600px, which is what stretched this card to the
      full viewport. `size="sm"` is the same 420 this used to declare.
    */
    p: t.modal.padding,
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
    borderRadius: t.modal.radius,
    background: t.modal.background,
    backdropFilter: t.modal.blur,
    WebkitBackdropFilter: t.modal.blur,
    border: t.modal.border,
    boxShadow: t.modal.shadow,
  },

  icon: (variant: "welcome" | "complete") => ({
    width: t.modal.iconSize,
    height: t.modal.iconSize,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius:
      variant === "welcome"
        ? t.modal.welcomeIconRadius
        : t.modal.completeIconRadius,
    background:
      variant === "welcome" ? t.modal.welcomeIconBg : t.modal.completeIconBg,
    color:
      variant === "welcome"
        ? t.modal.welcomeIconColor
        : t.modal.completeIconColor,
  }),

  title: {
    m: 0,
    fontSize: t.modal.titleFontSize,
    fontWeight: t.modal.titleFontWeight,
    color: t.modal.titleColor,
  },

  body: {
    m: 0,
    maxWidth: t.modal.bodyMaxWidth,
    fontSize: t.modal.bodyFontSize,
    lineHeight: t.modal.bodyLineHeight,
    color: t.modal.bodyColor,
  },

  actions: {
    display: "flex",
    flexDirection: "column",
    gap: t.modal.actionsGap,
    width: "100%",
    mt: t.modal.actionsMarginTop,
  },

  primaryButton: {
    "&.MuiButton-root": {
      width: "100%",
      height: t.modal.buttonHeight,
      borderRadius: t.modal.buttonRadius,
      gap: t.modal.buttonGap,
      background: t.actions.primaryBg,
      color: t.actions.primaryColor,
      textTransform: "none" as const,
      fontFamily: "inherit",
      fontSize: t.modal.buttonFontSize,
      fontWeight: t.modal.buttonFontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      boxShadow: "none",
      "&:hover": { background: t.actions.primaryHoverBg, boxShadow: "none" },
      ...focusVisible,
    },
  },

  secondaryButton: {
    "&.MuiButton-root": {
      width: "100%",
      height: t.modal.buttonHeight,
      borderRadius: t.modal.buttonRadius,
      border: t.actions.ghostBorder,
      background: t.actions.ghostBg,
      color: t.actions.ghostColor,
      textTransform: "none" as const,
      fontFamily: "inherit",
      fontSize: t.modal.buttonFontSize,
      fontWeight: t.modal.buttonFontWeight,
      letterSpacing: "normal",
      lineHeight: "normal",
      boxShadow: "none",
      "&:hover": {
        border: t.actions.ghostBorder,
        borderColor: t.actions.ghostHoverBorderColor,
        color: t.actions.ghostHoverColor,
        background: t.actions.ghostBg,
      },
      ...focusVisible,
    },
  },

  footnote: {
    m: 0,
    mt: t.modal.footnoteMarginTop,
    fontSize: t.modal.footnoteFontSize,
    color: t.modal.footnoteColor,
  },
} as const;
